import React, { Component } from 'react'
import { inject, observer } from 'mobx-react';
import { observable, toJS } from 'mobx';
import { Alert, Intent } from '@blueprintjs/core';
import Pagination from 'components/Pagination';
import Footer from 'components/Footer';
import Toast from 'helpers/toaster';
import { currencyFormattedAmount } from 'helpers/currency';
import * as DataList from 'components/DataList';

// Layout shared by the list and its loading skeleton (pages/service_type_extras/index.js).
export const SERVICE_TYPE_EXTRA_COLUMNS = [
  { area: 'name', label: 'Name', width: 'minmax(170px, 1.1fr)', bone: 'single' },
  { area: 'description', label: 'Description', width: 'minmax(200px, 1.6fr)', bone: 'single', icon: 'align-left' },
  { area: 'services', label: 'Service types', width: 'minmax(170px, 1.2fr)', bone: 'single', icon: 'tag' },
  { area: 'duration', label: 'Duration', width: '110px', bone: 'single', icon: 'time' },
  { area: 'price', label: 'Price', width: '110px', bone: 'single' },
  { area: 'actions', label: '', width: '104px', bone: 'none' },
];

export const SERVICE_TYPE_EXTRA_CARDS = {
  columns: 'minmax(0, 1fr) auto',
  areas: [
    'name actions',
    'description description',
    'services services',
    'duration duration',
    'price price',
  ],
  footer: ['price'],
};

export const SERVICE_TYPE_EXTRA_MIN_TABLE_WIDTH = 1040;

const hasValue = (value) => value !== null && value !== undefined && value !== '';

@inject('store', 'clientStore', 'serviceTypeExtraStore', 'viewStore') @observer
class ServiceTypeExtraTable extends Component {
  @observable isOpenAddEquipmentDialog = false;
  @observable isDeleteDialogVisible = false;
  @observable service;

  onPaginationChange(newPage) {
    let {
      viewStore: {
        serviceTypeExtraSearch
      }
    } = this.props;
    this.props.viewStore.serviceTypeExtraSearch = {
      ...serviceTypeExtraSearch,
      page: newPage
    };
    this.props.viewStore.serviceTypeExtras();
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
  }

  handleDeleteConfirm() {
    this.isDeleteDialogVisible = false;
    this.props.serviceTypeExtraStore.deleteServiceTypeExtra(this.service)
      .then(this.onSuccessfulDeleteServiceTypeExtra.bind(this))
      .catch(this.onFailDeleteServiceTypeExtra.bind(this))
  }

  onSuccessfulDeleteServiceTypeExtra(response) {
    Toast.show({ icon: 'thumbs-up', message: 'Service type extra deleted successfully!', intent: Intent.SUCCESS })
    this.service = null;
    this.isDeleteDialogVisible = false;
  }

  onFailDeleteServiceTypeExtra(error) {
    this.isDeleteDialogVisible = false;
    Toast.show({ icon: 'warning-sign', message: 'Unable to delete service type extra!', intent: Intent.DANGER })
  }

  handleServiceTypeClick(event, service) {
    switch (event.label) {
      case 'Edit':
        this.service = service;
        this.props.viewStore.serviceTypeExtraShow({uuid: service.attributes.id})
        break;
      case 'Delete':
        this.isDeleteDialogVisible = true;
        this.service = service;
        break;
      default:
    }
  }

  copyServiceTypeExtra(service) {
    let {
      props: {
        serviceTypeExtraStore: {
          serviceTypeExtraDetail,
          serviceTypeExtraDetail: { 
            object
           }
        }
      }
    } = this;
    
    serviceTypeExtraDetail.object = toJS(service); // a copy, so the row itself is untouched
    serviceTypeExtraDetail.object.id = '';
    serviceTypeExtraDetail.object.service_type_extra_services_attributes = [];
    if(serviceTypeExtraDetail.object.service_type_extra_services){
      serviceTypeExtraDetail.object.service_type_extra_services && serviceTypeExtraDetail.object.service_type_extra_services.map((values) => {
        return(
          serviceTypeExtraDetail.object.service_type_extra_services_attributes.push({
            service_type_id:values.service_type_id,
            label:values.label
          })
        )
      })
    }
    

    this.props.serviceTypeExtraStore
      .createServiceTypeExtra()
      .then(response => {
        Toast.show({
          icon: "thumbs-up",
          message: "Service type copied successfully!",
          intent: Intent.SUCCESS
        });
      })
      .catch(error => {
        Toast.show({
          icon: "warning-sign",
          message: "Unable to copy service type extra!",
          intent: Intent.DANGER
        });
      });
  }

  // Empty list's "Clear search": back to all extras, page 1.
  clearSearch() {
    const { viewStore } = this.props;
    viewStore.serviceTypeExtraSearch = { ...viewStore.serviceTypeExtraSearch, term: '*', page: 1 };
    viewStore.bumpListFilters();
    viewStore.serviceTypeExtras();
  }

  render() {
    let {
      viewStore,
      serviceTypeExtraStore: {
        serviceTypeExtra: {
          objects,
          meta
        }
      }
    } = this.props;
    const { serviceTypeExtraSearch } = viewStore;
    const term = serviceTypeExtraSearch.term && serviceTypeExtraSearch.term !== '*' ? serviceTypeExtraSearch.term : '';

    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="extras"
          term={term}
          onClearFilters={this.clearSearch.bind(this)}
          extraAction={!term && { label: 'Add extra', icon: 'plus', onClick: () => viewStore.serviceTypeExtraAdd(), primary: true }}
        />
      );
    }
    return (
      <React.Fragment>
        <Footer>
          <div className="bp3-navbar-group bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
        <DataList.List
          columns={SERVICE_TYPE_EXTRA_COLUMNS}
          cards={SERVICE_TYPE_EXTRA_CARDS}
          minTableWidth={SERVICE_TYPE_EXTRA_MIN_TABLE_WIDTH}
          total={meta.total_records || objects.length}
          noun="extras"
          note={DataList.sortNote(serviceTypeExtraSearch.order)}
        >
          {objects.map((services) => {
            const attributes = services.attributes;
            const serviceNames = (attributes.service_types || []).map((service) => service.name).filter(Boolean);
            return (
              <DataList.Row key={services.id}>
                <DataList.Cell area="name">
                  <DataList.Lines
                    title={attributes.name}
                    primary={
                      <DataList.RowLink
                        href={`/dashboard/service-type-extra/${attributes.id}/show`}
                        label={`Extra ${attributes.name}`}
                      >
                        {attributes.name}
                      </DataList.RowLink>
                    }
                  />
                </DataList.Cell>
                <DataList.Cell area="description">
                  {attributes.description ? (
                    <DataList.Lines primary={attributes.description} strong={false} />
                  ) : (
                    <DataList.Muted>No description</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="services">
                  {serviceNames.length > 0 ? (
                    <DataList.Lines primary={serviceNames.join(', ')} strong={false} />
                  ) : (
                    <DataList.Muted>No service types</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="duration">
                  {hasValue(attributes.duration) ? (
                    <DataList.Lines primary={String(attributes.duration)} strong={false} />
                  ) : (
                    <DataList.Muted>—</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="price">
                  {hasValue(attributes.price) ? (
                    <span className="dl-lines__primary">
                      <DataList.CardOnly>Price </DataList.CardOnly>
                      {currencyFormattedAmount(attributes.price)}
                    </span>
                  ) : (
                    <DataList.Muted>—</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      { icon: 'duplicate', label: 'Duplicate extra', onClick: () => this.copyServiceTypeExtra(attributes) },
                      { icon: 'edit', label: 'Edit extra', onClick: () => viewStore.serviceTypeExtraShow({ uuid: attributes.id }) },
                      { icon: 'trash', label: 'Delete extra', intent: 'danger', onClick: () => this.handleServiceTypeClick({ label: 'Delete' }, services) },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleDeleteCancel.bind(this)}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <p>Warnning! Deleting this service type will not be recoverable.</p>
          <p>Are you sure you want to do this?</p>
        </Alert>

        <Footer>
          <div className="bp3-navbar-group bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>

      </React.Fragment>
    )
  }

}

export default ServiceTypeExtraTable;